/**
 * Page-local URL params for OutreachPage's three list-affecting pieces of state that were NOT
 * surviving back-navigation or a refresh: the active tab, the DataTable page size, and the
 * current page number. Tenth adoption of the pattern #78 established (after #97-#102, #104,
 * #105).
 *
 * The deep-link params (`clientId`, `bucket`, `returnType` -- arriving from
 * InHouseClientsPage/PriorityOutreachPage "send to outreach" links) already live in
 * `useSearchParams()` directly on the page as read-only triggers, not real list state: out of
 * scope by construction, nothing to fix there.
 *
 * Deliberately OUT of scope: `selectedClients` (bulk-send row selection), the send/preview/
 * enhanced-send dialogs, the history preview panel, and `currentPageClients` (a DataTable
 * `onPageDataChange` callback value, not something a user sets) -- same calls made elsewhere in
 * this sweep.
 *
 * Conventions (mirroring the other hooks in this sweep):
 * - `tab=<value>` present only when non-default ('clients'); invalid falls back to 'clients'.
 * - `pageSize=<n>` present only when non-default (25); must be one of DataTable's own supported
 *   sizes (25/50/100) or it falls back to 25 -- never hands DataTable a size it doesn't support.
 * - `page=<n>` present only when non-default (1); non-numeric or non-positive falls back to 1.
 * - `q=<text>` (§16.5, added 2026-11-02): the one global search param, present only when non-empty,
 *   mirroring `useTaxReturnsScopeParams`'s own `q`/`setQ`. Changing it resets page back to 1.
 * - `filter.<col>.<op>=<value>` (steps 7/8, added 2026-11-02): the column filters, SAME encoding
 *   `useListSearchParams` uses fleet-wide (contract §1.1) -- this hook grew its own `filters`/
 *   `setFilters` instead of running a second, parallel `useListSearchParams` instance on this
 *   page's URL, which this file's own prior revision named as the reason filters stayed local
 *   `useState`: two independent hooks each calling `setSearchParams` on the SAME URL is a real
 *   last-write-wins race if both fire in one tick, not something to wire in without resolving
 *   that collision first. One hook owning the whole URL removes the race outright. Saved views
 *   (step 7) are exactly this filter encoding (plus `q`) under a name; the landing default (step
 *   8) reads/writes `core.StaffScreenState` through `hooks/useScreenStateDefault.ts` (filter
 *   contract v3 §16.1, 2026-11-02) -- which owns ITS OWN `useSearchParams()` call rather than
 *   going through this hook, the same pattern ptc-core's own EntityListPage uses, since the
 *   landing-apply effect only ever fires once, before this page's own setters have a chance to
 *   run.
 * - Changing tab, pageSize, q, filters or sort resets page back to 1 (you don't want page 4 of a
 *   tab/size/search/filter/sort set you just switched into).
 * - Mutations replace history (never push).
 * - `sort=<col>&dir=<asc|desc>` (fleet filter contract v3 §13.1, M1, added 2026-11-03): the
 *   client list's explicit column-menu sort, same `sort`/`dir` grammar `useListSearchParams` uses
 *   fleet-wide -- owned here, not by a second hook, for the same reason `filters`/`q` are: one
 *   hook on this page's URL, no two-writer race. This page has no date-range column (confirmed by
 *   grep of its own `filterColumns`), so it correctly carries no `group`/`groupBy` (C66) -- only
 *   Tax Returns' `scheduledDate` column groups.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PageSize } from '../components/DataTable';

const VALID_PAGE_SIZES: readonly PageSize[] = [25, 50, 100];
const DEFAULT_PAGE_SIZE: PageSize = 25;
const FILTER_PREFIX = 'filter.';

export interface OutreachScopeParams {
  tab: string;
  pageSize: PageSize;
  page: number;
  q: string;
  filters: Record<string, string>;
  /** Explicit sort override from the column menu; `null` = DataTable's own default/uncontrolled sort. */
  sort: { col: string; dir: 'asc' | 'desc' } | null;
  setTab: (tab: string) => void;
  setPageSize: (pageSize: PageSize) => void;
  setPage: (page: number) => void;
  setQ: (q: string) => void;
  setFilters: (next: Record<string, string>) => void;
  setSort: (sort: { col: string; dir: 'asc' | 'desc' } | null) => void;
  /** Step 8: replace q + filters in one atomic URL write (landing default / saved-view restore),
   * so applying a remembered or saved state is one history entry, not two races. */
  applyRemembered: (next: { q?: string; filters?: Record<string, string> }) => void;
}

export function useOutreachScopeParams(): OutreachScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo(() => {
    const raw = searchParams.get('tab');
    return raw === 'history' ? 'history' : 'clients';
  }, [searchParams]);

  const pageSize = useMemo<PageSize>(() => {
    const raw = searchParams.get('pageSize');
    const parsed = raw ? Number(raw) : NaN;
    return (VALID_PAGE_SIZES as number[]).includes(parsed) ? (parsed as PageSize) : DEFAULT_PAGE_SIZE;
  }, [searchParams]);

  const page = useMemo(() => {
    const raw = searchParams.get('page');
    const parsed = raw ? Number(raw) : NaN;
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
  }, [searchParams]);

  const q = searchParams.get('q') || '';

  const filters = useMemo(() => {
    const next: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith(FILTER_PREFIX)) next[key] = value;
    });
    return next;
  }, [searchParams]);

  const sort = useMemo(() => {
    const col = searchParams.get('sort');
    if (!col) return null;
    return { col, dir: searchParams.get('dir') === 'desc' ? ('desc' as const) : ('asc' as const) };
  }, [searchParams]);

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setTab = useCallback(
    (next: string) => {
      update((params) => {
        if (next === 'clients') params.delete('tab');
        else params.set('tab', next);
        params.delete('page');
      });
    },
    [update]
  );

  const setPageSize = useCallback(
    (next: PageSize) => {
      update((params) => {
        if (next === DEFAULT_PAGE_SIZE) params.delete('pageSize');
        else params.set('pageSize', String(next));
        params.delete('page');
      });
    },
    [update]
  );

  const setPage = useCallback(
    (next: number) => {
      update((params) => {
        if (next === 1) params.delete('page');
        else params.set('page', String(next));
      });
    },
    [update]
  );

  const setQ = useCallback(
    (next: string) => {
      update((params) => {
        if (!next) params.delete('q');
        else params.set('q', next);
        params.delete('page');
      });
    },
    [update]
  );

  const setFilters = useCallback(
    (nextFilters: Record<string, string>) => {
      update((params) => {
        for (const key of [...params.keys()]) {
          if (key.startsWith(FILTER_PREFIX)) params.delete(key);
        }
        for (const [key, value] of Object.entries(nextFilters)) params.set(key, value);
        params.delete('page');
      });
    },
    [update]
  );

  const setSort = useCallback(
    (next: { col: string; dir: 'asc' | 'desc' } | null) => {
      update((params) => {
        if (next) {
          params.set('sort', next.col);
          params.set('dir', next.dir);
        } else {
          params.delete('sort');
          params.delete('dir');
        }
        params.delete('page');
      });
    },
    [update]
  );

  const applyRemembered = useCallback(
    (next: { q?: string; filters?: Record<string, string> }) => {
      update((params) => {
        for (const key of [...params.keys()]) {
          if (key.startsWith(FILTER_PREFIX)) params.delete(key);
        }
        if (next.filters) {
          for (const [key, value] of Object.entries(next.filters)) params.set(key, value);
        }
        if (next.q) params.set('q', next.q);
        else params.delete('q');
        params.delete('page');
      });
    },
    [update]
  );

  return { tab, pageSize, page, q, filters, sort, setTab, setPageSize, setPage, setQ, setFilters, setSort, applyRemembered };
}
